interface EnvironmentConfig {
  // App Info
  appName: string
  appUrl: string
  environment: 'development' | 'staging' | 'production'
  
  // Supabase
  supabaseUrl: string
  supabaseAnonKey: string
  
  // API
  apiUrl: string
  wsUrl: string
  apiTimeout: number
  
  // External Services
  stripePublishableKey: string
  ipQualityScoreKey: string
  
  // Feature Flags
  features: {
    enableRealTimeAlerts: boolean
    enableMachineLearning: boolean
    enableBulkOperations: boolean
    enableAdvancedAnalytics: boolean
    enableTeamFeatures: boolean
    enableBilling: boolean
    maintenanceMode: boolean
  }
  
  // Limits
  limits: {
    maxLeadsPerRequest: number
    maxExportRows: number
    maxTeamMembers: number
    maxApiRequestsPerMinute: number
  }
  
  // Development
  mockMode: boolean
  debugMode: boolean
  logLevel: 'error' | 'warn' | 'info' | 'debug'
}

// Environment detection
const getEnvironment = (): 'development' | 'staging' | 'production' => {
  const hostname = window.location.hostname
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development'
  }
  if (hostname.includes('staging') || hostname.includes('test')) {
    return 'staging'
  }
  return 'production'
}

// Configuration loader
export const config: EnvironmentConfig = {
  // App Info
  appName: import.meta.env.VITE_APP_NAME || 'Fraud Detection System',
  appUrl: import.meta.env.VITE_APP_URL || window.location.origin,
  environment: getEnvironment(),
  
  // Supabase
  supabaseUrl: import.meta.env.VITE_SUPABASE_URL || '',
  supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
  
  // API
  apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:8089',
  wsUrl: import.meta.env.VITE_WS_URL || 'ws://localhost:8089',
  apiTimeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
  
  // External Services (publishable keys are safe in frontend)
  stripePublishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '',
  ipQualityScoreKey: import.meta.env.VITE_IPQS_KEY || '',
  
  // Feature Flags (can be overridden by environment)
  features: {
    enableRealTimeAlerts: import.meta.env.VITE_ENABLE_REALTIME !== 'false',
    enableMachineLearning: import.meta.env.VITE_ENABLE_ML === 'true',
    enableBulkOperations: import.meta.env.VITE_ENABLE_BULK !== 'false',
    enableAdvancedAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS !== 'false',
    enableTeamFeatures: import.meta.env.VITE_ENABLE_TEAM !== 'false',
    enableBilling: import.meta.env.VITE_ENABLE_BILLING !== 'false',
    maintenanceMode: import.meta.env.VITE_MAINTENANCE_MODE === 'true'
  },
  
  // Limits
  limits: {
    maxLeadsPerRequest: parseInt(import.meta.env.VITE_MAX_LEADS_PER_REQUEST || '100'),
    maxExportRows: parseInt(import.meta.env.VITE_MAX_EXPORT_ROWS || '10000'),
    maxTeamMembers: parseInt(import.meta.env.VITE_MAX_TEAM_MEMBERS || '50'),
    maxApiRequestsPerMinute: parseInt(import.meta.env.VITE_MAX_API_REQUESTS || '60')
  },
  
  // Development
  mockMode: import.meta.env.VITE_MOCK_MODE === 'true',
  debugMode: import.meta.env.VITE_DEBUG === 'true',
  logLevel: (import.meta.env.VITE_LOG_LEVEL as any) || 'info'
}

// Validation
export const validateConfig = (): boolean => {
  // Since Supabase keys are hardcoded in client.ts, just validate API URL
  const isValid = config.apiUrl && config.appName
  
  if (!isValid) {
    console.warn('Some configuration values are missing, using defaults')
  }
  
  return true
}

// Export individual configs for convenience
export const isDevelopment = config.environment === 'development'
export const isProduction = config.environment === 'production'
export const isStaging = config.environment === 'staging'